/**
 * `tasks.ts`（v3.15）—— 新手引导任务中心。
 *
 * 7 项引导任务，完成条件实时检测（解锁/签到/好友/群为动态状态，
 * 发帖/关注官方号为本地显式标记）。进度仅做引导，不发额外链上奖励。
 */

/** 本地显式标记键（发帖 / 关注官方号 / 绑定推荐，跨会话保留）。 */
const TASK_MARK_KEYS = {
  posted: 'mesh_task_posted',
  followedOfficial: 'mesh_task_followed_official',
  referralBound: 'mesh_referral_bound',
} as const;

export function markTaskDone(key: keyof typeof TASK_MARK_KEYS): void {
  try {
    window.localStorage.setItem(TASK_MARK_KEYS[key], '1');
  } catch {
    /* 存储满忽略 */
  }
}

export function isTaskMarked(key: keyof typeof TASK_MARK_KEYS): boolean {
  try {
    return window.localStorage.getItem(TASK_MARK_KEYS[key]) === '1';
  } catch {
    return false;
  }
}

export type TaskId =
  | 'unlock'
  | 'referral'
  | 'checkin'
  | 'post'
  | 'contact'
  | 'official'
  | 'group';

export interface TaskDef {
  id: TaskId;
  icon: string;
  title: string;
  desc: string;
}

/** 引导任务清单（顺序即推荐完成顺序）。 */
export const NEWCOMER_TASKS: TaskDef[] = [
  { id: 'unlock', icon: '🔓', title: '解锁身份', desc: '用口令解锁你的去中心化身份' },
  { id: 'referral', icon: '🎁', title: '绑定推荐人', desc: '绑定邀请你加入的好友，双方获推荐奖励' },
  { id: 'checkin', icon: '📅', title: '首次签到', desc: '每日签到即挖矿（0.0005 DPB 起）' },
  { id: 'post', icon: '✍️', title: '发第一条内容', desc: '发帖上链，进入推荐流并获得行为奖励' },
  { id: 'contact', icon: '👥', title: '加第一个好友', desc: '扫码或输入 PeerID 添加好友' },
  { id: 'official', icon: '⭐', title: '关注官方号', desc: '关注 MeshLink 官方，随时获取公告' },
  { id: 'group', icon: '💬', title: '加入一个群', desc: '建群或通过邀请链接加入群聊' },
];

/** 计算任务完成状态（动态状态由调用方传入）。 */
export function computeTaskDone(id: TaskId, ctx: {
  unlocked: boolean;
  checkinToday: boolean;
  contactCount: number;
  groupCount: number;
}): boolean {
  switch (id) {
    case 'unlock':
      return ctx.unlocked;
    case 'referral':
      return isTaskMarked('referralBound');
    case 'checkin':
      return ctx.checkinToday;
    case 'post':
      return isTaskMarked('posted');
    case 'contact':
      return ctx.contactCount >= 1;
    case 'official':
      return isTaskMarked('followedOfficial');
    case 'group':
      return ctx.groupCount >= 1;
    default:
      return false;
  }
}
